import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import {
  init, backButton,
  mockTelegramEnv, parseInitData
} from '@telegram-apps/sdk-react';
import { QueryProvider } from './providers/QueryProvider';
import AppBootstrap from './components/AppBootstrap';
import {
  getAuthMode,
  getBrowserAuthUser,
} from './utils/telegram-environment';
import { setupTelegramMiniApp } from './utils/setupTelegramMiniApp';

/**
 * Initialize Telegram SDK based on environment mode
 * 
 * - In Telegram mode: SDK initializes normally
 * - In Browser mode with authenticated user: Mock SDK with stored user data
 * - In Browser mode without auth: Mock SDK with placeholder (will show login page)
 * - In DEV mode: Can use hardcoded mock for development
 */
const initializeTelegramSdk = () => {
  const authMode = getAuthMode();
  
  console.log('[Main] Initializing Telegram SDK, mode:', authMode);
  

  // DEV mode override for local development testing
  if (import.meta.env.DEV) {
    const devInitDataRaw = new URLSearchParams([
      ['user', JSON.stringify({
        id: 5675692625,
        first_name: 'Andrew',
        last_name: 'Rogue',
        username: 'rogue',
        language_code: 'en',
        is_premium: true,
        allows_write_to_pm: true,
      })],
      ['signature', '4a2652bc8b20d3ddd9c71a7ebd28687be980a8b90825c539422d2ba2e64180fc'],
      ['hash', '4a2652bc8b20d3ddd9c71a7ebd28687be980a8b90825c539422d2ba2e64180fc'],
      ['auth_date', '1716922846'],
      ['start_param', ''],
      ['chat_type', 'sender'],
      ['chat_instance', '8428209589180549439'],
    ]).toString();

    mockTelegramEnv({
      themeParams: {
        accentTextColor: '#6ab2f2',
        bgColor: '#17212b',
        buttonColor: '#5288c1',
        buttonTextColor: '#ffffff',
        destructiveTextColor: '#ec3942',
        headerBgColor: '#17212b',
        hintColor: '#708499',
        linkColor: '#6ab3f3',
        secondaryBgColor: '#232e3c',
        sectionBgColor: '#17212b',
        sectionHeaderTextColor: '#6ab3f3',
        subtitleTextColor: '#708499',
        textColor: '#f5f5f5',
      },
      initData: parseInitData(devInitDataRaw),
      initDataRaw: devInitDataRaw,
      version: '7.2',
      platform: 'tdesktop',
    });

    console.log('[Main] DEV mode: Mocked Telegram SDK with hardcoded user');
  }


  // If running in browser mode (not inside Telegram)
  if (authMode === 'browser') {
    // Check if we have a stored browser auth user
    const browserUser = getBrowserAuthUser();
    
    // Use a placeholder signature for SDK compatibility
    const placeholderSignature = '0000000000000000000000000000000000000000000000000000000000000000';
    
    if (browserUser) {
      // User is already authenticated in browser mode
      // Create mock init data with all required fields for SDK compatibility
      const initDataRaw = new URLSearchParams([
        ['user', JSON.stringify({
          id: browserUser.id,
          first_name: browserUser.first_name || '',
          last_name: browserUser.last_name || '',
          username: browserUser.username || '',
          language_code: 'en',
          is_premium: false,
          allows_write_to_pm: true,
        })],
        ['hash', browserUser.hash],
        ['signature', placeholderSignature],
        ['auth_date', browserUser.auth_date],
        ['chat_type', 'sender'],
        ['chat_instance', '8428209589180549439'],
      ]).toString();

      mockTelegramEnv({
        themeParams: {
          accentTextColor: '#6ab2f2',
          bgColor: '#17212b',
          buttonColor: '#5288c1',
          buttonTextColor: '#ffffff',
          destructiveTextColor: '#ec3942',
          headerBgColor: '#17212b',
          hintColor: '#708499',
          linkColor: '#6ab3f3',
          secondaryBgColor: '#232e3c',
          sectionBgColor: '#17212b',
          sectionHeaderTextColor: '#6ab3f3',
          subtitleTextColor: '#708499',
          textColor: '#f5f5f5',
        },
        initData: parseInitData(initDataRaw),
        initDataRaw,
        version: '7.2',
        platform: 'tdesktop',
      });

      console.log('[Main] Mocked Telegram SDK for browser authenticated user');
    } else {
      // Not authenticated yet - create placeholder mock to prevent SDK errors
      // The login page will be shown by AppBootstrap
      const placeholderInitDataRaw = new URLSearchParams([
        ['user', JSON.stringify({
          id: 0,
          first_name: 'Browser',
          last_name: 'User',
          username: 'browser_user',
          language_code: 'en',
          is_premium: false,
          allows_write_to_pm: true,
        })],
        ['hash', placeholderSignature],
        ['signature', placeholderSignature],
        ['auth_date', Math.floor(Date.now() / 1000).toString()],
        ['chat_type', 'sender'],
        ['chat_instance', '8428209589180549439'],
      ]).toString();

      mockTelegramEnv({
        themeParams: {
          accentTextColor: '#6ab2f2',
          bgColor: '#17212b',
          buttonColor: '#5288c1',
          buttonTextColor: '#ffffff',
          destructiveTextColor: '#ec3942',
          headerBgColor: '#17212b',
          hintColor: '#708499',
          linkColor: '#6ab3f3',
          secondaryBgColor: '#232e3c',
          sectionBgColor: '#17212b',
          sectionHeaderTextColor: '#6ab3f3',
          subtitleTextColor: '#708499',
          textColor: '#f5f5f5',
        },
        initData: parseInitData(placeholderInitDataRaw),
        initDataRaw: placeholderInitDataRaw,
        version: '7.2',
        platform: 'tdesktop',
      });

      console.log('[Main] Mocked Telegram SDK with placeholder for browser login');
    }
  }

};

// Initialize the Telegram SDK with environment-aware mocking
initializeTelegramSdk();

// Initialize the Telegram SDK package
init();

// Mount the Back Button, so we will work with 
// the actual component properties.
backButton.mount();

void setupTelegramMiniApp();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryProvider>
      <AppBootstrap />
    </QueryProvider>
  </React.StrictMode>,
)
